/**
 * ThumbnailNav - 썸네일 네비게이션 바
 *
 * Heyzine 스타일의 하단 썸네일 네비게이션
 * PDF/EPUB 페이지 미리보기 및 빠른 이동
 *
 * 홍익인간 (Hongik Ingan) - WIA Standards
 */

import { isPageVisible, pageLabel } from '../../utils/spreadMap';
import { useState, useRef, useEffect, useCallback } from 'react';
import { FiChevronLeft, FiChevronRight, FiGrid } from 'react-icons/fi';

interface ThumbnailItem {
  pageNumber: number;
  thumbnail?: string;  // base64 또는 URL
  label?: string;
}

interface ThumbnailNavProps {
  isOpen: boolean;
  onToggle: () => void;
  totalPages: number;
  currentPage: number;
  thumbnails: ThumbnailItem[];
  onPageSelect: (pageNumber: number) => void;
  fileType: 'epub' | 'pdf';
  // V1: 이 바는 position:fixed라 문서 흐름을 안 밀어낸다 — 열렸을 때 실측 높이를
  // 콜백으로 올려줘야 FlipbookView가 책 크기를 계산할 때 겹치지 않게 뺄 수 있다.
  onHeightChange?: (heightPx: number) => void;
  // R3(SPRINT 1.9 QA): FlipbookView의 footer(하단 네비게이션) 실측 높이. 닫힘 상태
  // 토글 배지가 이 값 위로 벗어나도록 자기 위치를 계산한다(FlipbookView가 없는
  // 스크롤 모드에서는 0).
  footerClearancePx?: number;
  // S2-1: 세로(단면) 모드인지 — 스프레드 강조 범위 계산에 필요.
  isPortrait?: boolean;
}

// S2-1(G3): 스프레드 모드에서는 한 번에 두 쪽이 보이므로 강조도 두 쪽 다 해야
// 한다. 매핑은 FlipbookView와 같은 정본 유틸을 쓴다.
const ThumbnailNav = ({
  isOpen,
  onToggle,
  totalPages,
  currentPage,
  thumbnails,
  onPageSelect,
  fileType: _fileType, // 향후 타입별 썸네일 스타일링에 사용
  onHeightChange,
  footerClearancePx = 0,
  isPortrait = false,
}: ThumbnailNavProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // V1: 열려 있을 때만 실제 렌더된 높이를 보고한다. 닫히면(translate-y-full로 화면
  // 밖에 나가 있을 뿐 DOM에서 사라지진 않음) 0을 보고해 FlipbookView가 다시 전체
  // 공간을 쓰게 한다.
  useEffect(() => {
    if (!onHeightChange) return;
    if (!isOpen) {
      onHeightChange(0);
      return;
    }
    const el = barRef.current;
    if (!el) return;
    const report = () => onHeightChange(el.getBoundingClientRect().height);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen, onHeightChange]);

  // 현재 페이지로 스크롤
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      const thumbnailWidth = 80 + 8; // width + gap
      const targetScroll = (currentPage - 1) * thumbnailWidth - scrollRef.current.offsetWidth / 2 + thumbnailWidth / 2;
      scrollRef.current.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth',
      });
    }
  }, [isOpen, currentPage]);

  // 드래그 스크롤
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // 페이지 네비게이션 버튼
  const scrollToPrev = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollToNext = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // 썸네일 없을 때 기본 표시
  const renderThumbnail = (item: ThumbnailItem) => {
    if (item.thumbnail) {
      return (
        <img
          src={item.thumbnail}
          alt={`페이지 ${item.pageNumber}`}
          className="w-full h-full object-cover"
          draggable={false}
        />
      );
    }

    // 기본 플레이스홀더
    return (
      <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">
          {item.pageNumber}
        </span>
      </div>
    );
  };

  // R3(SPRINT 1.9 QA): 닫힘 상태 배지가 FlipbookView의 footer(하단 네비게이션)와 같은
  // 화면 영역에 겹쳐 줌 배율 숫자를 가리는 회귀가 있었다(실측: 배지 992~1028px,
  // footer 943~1040px — 완전히 겹침). footerClearancePx(footer 실측 높이)를 받으면
  // 그 위로 확실히 벗어나도록 오프셋을 계산한다 — 자동재생 속도 선택 등으로 footer가
  // 커져도 고정 px이 아니라 실측값 기준이라 다시 안 겹친다. 배지 자체 높이(~40px)와
  // 여유 16px을 더한다. footerClearancePx가 없으면(스크롤 모드 등 FlipbookView 자체가
  // 없을 때) 기존 고정값(-top-12, 48px)을 그대로 쓴다.
  const hasFooterClearance = footerClearancePx > 0;
  const closedOffsetPx = footerClearancePx + 40 + 16;

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
      {/* Toggle Button — R3: "N / total"은 아래 슬라이더 행에 이미 있어 중복이라 제거,
          아이콘 단독 배지로 축소(접근성 정보는 aria-label/title로 유지) */}
      <button
        onClick={onToggle}
        aria-label={`페이지 목록 열기 (${pageLabel(currentPage, totalPages, isPortrait)} / ${totalPages})`}
        title={`페이지 목록 (${pageLabel(currentPage, totalPages, isPortrait)} / ${totalPages})`}
        className={`absolute left-1/2 -translate-x-1/2 p-2.5 bg-white dark:bg-gray-800 rounded-t-lg shadow-lg flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${isOpen ? '-top-10' : (hasFooterClearance ? '' : '-top-12')}`}
        style={!isOpen && hasFooterClearance ? { top: `-${closedOffsetPx}px` } : undefined}
      >
        <FiGrid size={18} />
      </button>

      {/* Thumbnail Bar */}
      <div ref={barRef} className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-2xl">
        <div className="relative flex items-center p-3">
          {/* Left Arrow */}
          <button
            onClick={scrollToPrev}
            className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors mr-2"
          >
            <FiChevronLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>

          {/* Thumbnails Container */}
          <div
            ref={scrollRef}
            className={`flex-1 flex gap-2 overflow-x-auto scrollbar-hide ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={handleMouseDown}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {thumbnails.map((item) => (
              <button
                key={item.pageNumber}
                onClick={() => !isDragging && onPageSelect(item.pageNumber)}
                aria-label={`페이지 ${item.pageNumber}`}
                aria-current={isPageVisible(item.pageNumber, currentPage, totalPages, isPortrait) ? 'page' : undefined}
                className={`flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden border-2 transition-all ${
                  isPageVisible(item.pageNumber, currentPage, totalPages, isPortrait)
                    ? 'border-primary ring-2 ring-primary/30 scale-105'
                    : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {renderThumbnail(item)}
              </button>
            ))}

            {/* 썸네일이 없을 때 페이지 번호만 표시 */}
            {thumbnails.length === 0 && totalPages > 0 && (
              Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => !isDragging && onPageSelect(pageNum)}
                  aria-label={`페이지 ${pageNum}`}
                  aria-current={isPageVisible(pageNum, currentPage, totalPages, isPortrait) ? 'page' : undefined}
                  className={`flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden border-2 transition-all ${
                    isPageVisible(pageNum, currentPage, totalPages, isPortrait)
                      ? 'border-primary ring-2 ring-primary/30 scale-105'
                      : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <span className="text-lg font-bold text-gray-500 dark:text-gray-400">
                      {pageNum}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Right Arrow */}
          <button
            onClick={scrollToNext}
            className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ml-2"
          >
            <FiChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(currentPage / totalPages) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ThumbnailNav;
